import { useMsal } from "@azure/msal-react";
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown, IDropdownOption, MessageBar, MessageBarType, PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack, Text, TextField
} from "@fluentui/react";
import { RootState } from "app/store";
import { emitCustomEvent, fadeIn } from "global/Application";
import { SaveAsync } from "global/Application/dataActions";
import { useAuthentication } from "global/Authentication";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { timestamp } from "global/Utility/TSFunctions";
import {
    bcbs_enrollmentdetails as IXrmTypes_bcbs_enrollmentdetails
} from "model/CRM";
import { Ibcbs_enrollmentdetailExtended } from "model/CRM.type";
import {
    IOptionSetValue as FAM_IOptionSetValue
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";


interface IEnrollmentDetailDialogProps extends TComponentConnectedProps {
    isOpen: boolean;
    onDismiss?: () => void;
    onConfirm?: () => void;
    softDismiss?: boolean;
}

const EnrollmentDetailDialog: React.FC<IEnrollmentDetailDialogProps> = (props) => {

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);
    const authentication = useAuthentication();
    const dispatch = useDispatch();

    const [enrollDetails, setEnrollDetails] = useState<IXrmTypes_bcbs_enrollmentdetails | null>(null);

    const [viewModel, setViewModel] = useState({
        processing: false,
        validationMessage: null as string | null,

        // allows the component to perform a local visibility management, to help with the animation and ui smoothness
        // by default this should always be visible unless components has signaled a context job termination and wants to self close
        selfVisible: true,

        isNew: false,
    });

    useEffect(() => {
        // resets
        setViewModel(t => { return { ...t, processing: false, validationMessage: null, selfVisible: true } });

        if (props.isOpen) {

            (async () => {
                try {
                    setViewModel(t => { return { ...t, processing: true, validationMessage: null } });

                    if (props.state.data.snapshot?._bcbs_enrollmentdetailsid_value) {
                        const enrollmentDetail = await dataSource.getEnrollmentDetail(props.state.data.snapshot?._bcbs_enrollmentdetailsid_value);
                        setViewModel(t => { return { ...t, isNew: false } });
                        setEnrollDetails(enrollmentDetail);
                    } else {

                        setViewModel(t => { return { ...t, isNew: true } });

                        const recordEnrollmentDetail: Ibcbs_enrollmentdetailExtended = {
                            bcbs_name: `Group Configuration - ${timestamp()}`,
                            statuscode: 1,
                            statecode: 0,
                        };

                        const user = authentication.principal?.systemUser!;

                        if (user && user._bcbs_primaryteamid_value)
                            recordEnrollmentDetail['ownerid@odata.bind'] = `/teams(${user._bcbs_primaryteamid_value})`;

                        setEnrollDetails(recordEnrollmentDetail);

                    }

                } catch (error) {
                    throw error;
                } finally {
                    setViewModel(t => { return { ...t, processing: false } });
                }

            })();
        }
    }, [props.isOpen]);


    function setValidationMessage(validationMessage: string | null) {
        // resets
        setViewModel(t => { return { ...t, processing: false, validationMessage: validationMessage } });
    }
    return <Dialog
        minWidth={600}
        hidden={!(props.isOpen && viewModel.selfVisible)}
        onDismiss={props.onDismiss}
        dialogContentProps={{
            type: DialogType.largeHeader,
            title: 'Enrollment Detail',
            // subText: 'Please identify the configuration defaults for the new snapshot:',
        }}
        modalProps={{
            isBlocking: true,
            styles: { main: { maxWidth: 650 } },
        }}
    >
        <div style={{ paddingBottom: '15px' }}>

            {viewModel.processing &&
                <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
                        <Spinner size={SpinnerSize.large} />
                        <Text styles={{ root: { margin: '8px' } }} variant={'medium'}>Processing</Text>
                    </div>
                </div>
            }

            {!viewModel.processing &&
                <Stack tokens={{ childrenGap: '15px' }}>
                    {viewModel.validationMessage &&
                        <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                            {viewModel.validationMessage}
                        </MessageBar>
                    }

                    <Dropdown
                        disabled={viewModel.processing || enrollDetails === null}
                        label="ID Card Ordering"
                        options={props.state.data.config.cache.options_bcbs_idcardorderingoptioncode}
                        selectedKey={enrollDetails?.bcbs_idcardorderingoptioncode}
                        onChange={(event, option?: IDropdownOption<FAM_IOptionSetValue>) => {

                            if (option?.data?.value && enrollDetails)
                                setEnrollDetails(t => { return { ...t!, bcbs_idcardorderingoptioncode: option.data?.value } })
                        }}
                    />
                    <TextField
                        disabled={viewModel.processing || enrollDetails === null}
                        label="Membership Notes"
                        value={enrollDetails?.bcbs_membershipnotes}
                        onChange={(event, newValue) => {
                            setEnrollDetails(t => { return { ...t, bcbs_membershipnotes: newValue } })
                        }}
                        multiline
                        rows={5}
                        autoAdjustHeight={true}
                    />

                </Stack>
            }
        </div>

        <DialogFooter>

            <PrimaryButton
                disabled={viewModel.processing}
                text="Save"
                maxLength={2000}
                onClick={async () => {
                    try {

                        setViewModel(t => {
                            return {
                                ...t,
                                processing: true,
                            }
                        });

                        if (viewModel.isNew) {
                            // save enrollment detail
                            if (enrollDetails)
                                await SaveAsync(dispatch, dataSource, props.state, {
                                    onSaveCreateEnrollmentDetail: enrollDetails,
                                    ignoreJsonSave: true,
                                    onPostSaveIgnoreReload: false
                                });

                        }
                        else {

                            const change = {
                                bcbs_enrollmentdetailsid: enrollDetails?.bcbs_enrollmentdetailsid,
                                bcbs_idcardorderingoptioncode: enrollDetails?.bcbs_idcardorderingoptioncode,
                                bcbs_membershipnotes: enrollDetails?.bcbs_membershipnotes
                            }

                            await dataSource.updateEnrollmentDetail(enrollDetails?.bcbs_enrollmentdetailsid!, change);
                        }

                        if (props.softDismiss)
                            setViewModel(t => { return { ...t, selfVisible: false } });

                        if (props.onConfirm)
                            props.onConfirm();

                    } catch (error: any) {

                        setValidationMessage(`Saving enrollment details failed, see details. ${error}`);

                        // lets create a new one is payload is not providing one
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });

                    } finally {
                        setViewModel(t => { return { ...t, processing: false } });
                    }
                }}
            />

            <DefaultButton
                text="Cancel"
                onClick={() => {
                    props.onDismiss?.();
                }}
            />
        </DialogFooter>
    </Dialog>;
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(EnrollmentDetailDialog);
